/**
 * Forms and intake contracts (BUILD-PLAN M22, research/library/new-patient.md
 * section 4, documents.md DO-05 and DO-09). Owner: WP3-FORMS.
 *
 * The rules this file encodes:
 *
 * - A form is a versioned definition (`velora.form/1`). Answers bind to
 *   stable field keys, never to a version, so a new version pre-fills every
 *   key it shares with the old one (NP-18) and in-flight packets never lose
 *   answers when an admin publishes (NP-13).
 * - A field badged SYNC carries a `binding` to a record path. A field
 *   without one (NOTE) is stored on the submission only.
 * - A submitted form never changes the record by itself. Staff review a
 *   field-level diff and decide each row (NP-01); clinical rows default to
 *   "keep and flag" and an active alert is never removed because a patient
 *   answered "No" (NP-02).
 * - "Completed" is not "synced": every applied row reports where it went
 *   (Velora, a PMS command, or a manual PMS entry) and the readiness chip
 *   only turns green when nothing is pending or failed (NP-04).
 *
 * Pure helpers at the bottom (visibility, required checks, progress,
 * pre-fill across versions) run on both the patient page and the API.
 */
import { z } from 'zod';
import { TurnstileTokenSchema } from './turnstile.js';

const IdSchema = z.uuid();
const InstantSchema = z.iso.datetime({ offset: true });
const LocalDateSchema = z.iso.date();

/* ------------------------------------------------------------------------ */
/* Permissions                                                               */
/* ------------------------------------------------------------------------ */

/**
 * Permission keys the module checks. Existing catalog keys are reused until
 * identity adds dedicated ones (docs/requests/wp3-forms-permissions.md).
 */
export const FORMS_PERMISSIONS = {
  /** See forms, packets, submissions and the review queue. */
  view: 'patient.view',
  /** Send packets, review and accept non-clinical changes. */
  review: 'patient.edit',
  /** Accept insurance changes into coverage. */
  insurance: 'insurance.edit',
  /** Clinician actions: remove an alert, sign the medical-history review. */
  clinical: 'notes.sign',
  /** Build, edit and publish form templates. */
  manage: 'settings.manage',
} as const;

/* ------------------------------------------------------------------------ */
/* Form definition (velora.form/1)                                           */
/* ------------------------------------------------------------------------ */

export const FORM_FORMAT = 'velora.form/1' as const;

export const FORM_KINDS = [
  'intake',
  'medical_history',
  'consent',
  'hipaa',
  'financial',
  'other',
] as const;
export const FormKindSchema = z.enum(FORM_KINDS);
export type FormKind = z.infer<typeof FormKindSchema>;

export const FORM_KIND_LABEL: Record<FormKind, string> = {
  intake: 'Registration',
  medical_history: 'Medical history',
  consent: 'Consent',
  hipaa: 'Privacy',
  financial: 'Financial',
  other: 'Other',
};

export const FIELD_TYPES = [
  'short_text',
  'long_text',
  'email',
  'phone',
  'date',
  'select',
  'radio',
  'checkboxes',
  'yes_no',
  'checkbox',
  'consent',
  'signature',
  'insurance_card',
  'id_photo',
  'info',
] as const;
export const FieldTypeSchema = z.enum(FIELD_TYPES);
export type FieldType = z.infer<typeof FieldTypeSchema>;

export const FIELD_TYPE_LABEL: Record<FieldType, string> = {
  short_text: 'Short answer',
  long_text: 'Paragraph',
  email: 'Email',
  phone: 'Phone',
  date: 'Date',
  select: 'Dropdown',
  radio: 'Single choice',
  checkboxes: 'Multiple choice',
  yes_no: 'Yes / No / Unsure',
  checkbox: 'Checkbox',
  consent: 'Consent',
  signature: 'Signature',
  insurance_card: 'Insurance card',
  id_photo: 'Photo ID',
  info: 'Text block',
};

/** Field types that hold no answer. */
export const DISPLAY_ONLY_TYPES: ReadonlySet<FieldType> = new Set(['info']);

/**
 * Record paths a SYNC field can bind to. The category decides the default
 * review decision: demographics and contact default to accept, clinical and
 * insurance wait for an explicit decision.
 */
export const RECORD_PATHS = {
  'patient.first_name': { label: 'First name', category: 'demographics', types: ['short_text'] },
  'patient.last_name': { label: 'Last name', category: 'demographics', types: ['short_text'] },
  'patient.preferred_name': {
    label: 'Preferred name',
    category: 'demographics',
    types: ['short_text'],
  },
  'patient.birthdate': { label: 'Date of birth', category: 'demographics', types: ['date'] },
  'patient.address_line1': { label: 'Street address', category: 'contact', types: ['short_text'] },
  'patient.address_line2': {
    label: 'Apartment or unit',
    category: 'contact',
    types: ['short_text'],
  },
  'patient.city': { label: 'City', category: 'contact', types: ['short_text'] },
  'patient.state': { label: 'State', category: 'contact', types: ['short_text', 'select'] },
  'patient.postal_code': { label: 'ZIP code', category: 'contact', types: ['short_text'] },
  'patient.employer': { label: 'Employer', category: 'demographics', types: ['short_text'] },
  'contact.mobile': { label: 'Mobile phone', category: 'contact', types: ['phone'] },
  'contact.home_phone': { label: 'Home phone', category: 'contact', types: ['phone'] },
  'contact.email': { label: 'Email', category: 'contact', types: ['email'] },
  'consent.transactional_sms': {
    label: 'Appointment texts',
    category: 'consent',
    types: ['checkbox'],
  },
  'consent.marketing_sms': { label: 'Marketing texts', category: 'consent', types: ['checkbox'] },
  'consent.email': { label: 'Email messages', category: 'consent', types: ['checkbox'] },
  'medical.alert': { label: 'Medical alert', category: 'medical', types: ['yes_no'] },
  'coverage.primary': {
    label: 'Primary insurance',
    category: 'insurance',
    types: ['insurance_card'],
  },
} as const satisfies Record<
  string,
  { label: string; category: ReviewCategory; types: readonly FieldType[] }
>;
export type RecordPath = keyof typeof RECORD_PATHS;
export const RECORD_PATH_KEYS = Object.keys(RECORD_PATHS) as RecordPath[];
export const RecordPathSchema = z.enum(RECORD_PATH_KEYS as [RecordPath, ...RecordPath[]]);

export const REVIEW_CATEGORIES = [
  'demographics',
  'contact',
  'consent',
  'medical',
  'insurance',
] as const;
export type ReviewCategory = (typeof REVIEW_CATEGORIES)[number];
export const ReviewCategorySchema = z.enum(REVIEW_CATEGORIES);

export const REVIEW_CATEGORY_LABEL: Record<ReviewCategory, string> = {
  demographics: 'Personal',
  contact: 'Contact',
  consent: 'Messages and consent',
  medical: 'Medical',
  insurance: 'Insurance',
};

export const AlertKindSchema = z.enum(['medical', 'allergy', 'premed', 'medication', 'other']);
export type AlertKind = z.infer<typeof AlertKindSchema>;

/** The alert a `medical.alert` yes/no question maps to (SNOMED-coded where known). */
export const AlertBindingSchema = z.object({
  kind: AlertKindSchema,
  description: z.string().min(1).max(200),
  snomed: z
    .string()
    .regex(/^\d{6,18}$/)
    .nullable()
    .default(null),
  severity: z.enum(['info', 'warning', 'critical']).default('warning'),
});
export type AlertBinding = z.infer<typeof AlertBindingSchema>;

export const FieldOptionSchema = z.object({
  value: z
    .string()
    .min(1)
    .max(60)
    .regex(/^[a-z0-9_-]+$/i),
  label: z.string().min(1).max(120),
});
export type FieldOption = z.infer<typeof FieldOptionSchema>;

const FIELD_KEY_RE = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)*$/;
export const FieldKeySchema = z
  .string()
  .min(2)
  .max(80)
  .regex(FIELD_KEY_RE, 'field key: lower case words separated by dots or underscores');

export const ShowIfSchema = z.object({
  key: FieldKeySchema,
  /** Shown when the referenced answer equals this value (string, yes_no value or boolean). */
  equals: z.union([z.string(), z.boolean()]),
});
export type ShowIf = z.infer<typeof ShowIfSchema>;

export const FormFieldSchema = z.object({
  key: FieldKeySchema,
  type: FieldTypeSchema,
  label: z.string().min(1).max(300),
  help: z.string().max(600).optional(),
  required: z.boolean().default(false),
  options: z.array(FieldOptionSchema).max(60).optional(),
  binding: RecordPathSchema.optional(),
  alert: AlertBindingSchema.optional(),
  showIf: ShowIfSchema.optional(),
  /** Body text for `info` and `consent` fields (plain text, paragraphs split on blank lines). */
  text: z.string().max(8000).optional(),
  placeholder: z.string().max(120).optional(),
  /** `checkbox` default; marketing consent must default to false (NP-14). */
  defaultChecked: z.boolean().optional(),
});
export type FormField = z.infer<typeof FormFieldSchema>;

export const FormSectionSchema = z.object({
  id: z
    .string()
    .min(1)
    .max(40)
    .regex(/^[a-z][a-z0-9_-]*$/),
  title: z.string().min(1).max(120),
  description: z.string().max(600).optional(),
  fields: z.array(FormFieldSchema).max(80),
});
export type FormSection = z.infer<typeof FormSectionSchema>;

export const AttachRulesSchema = z.object({
  /** Send to new patients (first visit). */
  newPatient: z.boolean().default(false),
  /** Send when an appointment carries one of these CDT codes (consents by procedure). */
  cdtCodes: z
    .array(z.string().regex(/^D\d{4}$/))
    .max(80)
    .default([]),
  /** Re-send when the last accepted submission is older than this many months. */
  everyMonths: z.number().int().min(1).max(60).nullable().default(null),
  /**
   * Intake packet this form belongs to (Hayden C12, "New patient intake"):
   * when any form of a packet is suggested, the whole packet is, in
   * `packetOrder` order, so the patient gets one link with every form.
   */
  packet: z.string().trim().min(1).max(60).nullable().optional(),
  packetOrder: z.number().int().min(0).max(99).optional(),
  /**
   * Filing of the signed PDF (Hayden R21): the practice's document type
   * (its group in Velora's document list). Null: the default for the form kind.
   */
  documentTypeId: z.uuid().nullable().optional(),
  /**
   * The PMS document group the signed PDF belongs in (Eaglesoft "document
   * group"). Kept on the document as a `pms-group:` tag; no PMS has a
   * document write path yet, so staff file it there by hand until one does.
   */
  pmsDocumentGroup: z.string().trim().min(1).max(60).nullable().optional(),
});
export type AttachRules = z.infer<typeof AttachRulesSchema>;

export const FormDefinitionSchema = z
  .object({
    format: z.literal(FORM_FORMAT),
    title: z.string().min(1).max(120),
    description: z.string().max(1000).optional(),
    kind: FormKindSchema,
    language: z.enum(['en', 'es']).default('en'),
    sections: z.array(FormSectionSchema).min(1).max(30),
  })
  .superRefine((def, ctx) => {
    const issues = definitionIssues(def as FormDefinition);
    for (const message of issues) ctx.addIssue({ code: 'custom', message });
  });
export type FormDefinition = {
  format: typeof FORM_FORMAT;
  title: string;
  description?: string | undefined;
  kind: FormKind;
  language: 'en' | 'es';
  sections: FormSection[];
};

/**
 * Structural checks a schema cannot express: unique keys and section ids,
 * options on choice fields, bindings that suit the field type, alerts only
 * on medical questions, `showIf` pointing at an earlier field.
 */
export function definitionIssues(def: FormDefinition): string[] {
  const issues: string[] = [];
  const seen = new Set<string>();
  const sections = new Set<string>();
  const bindings = new Set<string>();
  for (const section of def.sections) {
    if (sections.has(section.id)) issues.push(`section id "${section.id}" is used twice`);
    sections.add(section.id);
    for (const f of section.fields) {
      if (seen.has(f.key)) issues.push(`field key "${f.key}" is used twice`);
      const choice = f.type === 'select' || f.type === 'radio' || f.type === 'checkboxes';
      if (choice && (!f.options || f.options.length < 2)) {
        issues.push(`"${f.label}" needs at least two options`);
      }
      if (f.binding) {
        const spec = RECORD_PATHS[f.binding];
        if (!(spec.types as readonly string[]).includes(f.type)) {
          issues.push(`"${f.label}" (${f.type}) cannot fill ${spec.label}`);
        }
        if (f.binding !== 'medical.alert') {
          if (bindings.has(f.binding)) issues.push(`two fields fill ${spec.label}`);
          bindings.add(f.binding);
        }
      }
      if (f.binding === 'medical.alert' && !f.alert) {
        issues.push(`"${f.label}" fills a medical alert but names no alert`);
      }
      if (f.alert && f.binding !== 'medical.alert') {
        issues.push(`"${f.label}" names an alert but is not bound to medical alerts`);
      }
      if (f.showIf && !seen.has(f.showIf.key)) {
        issues.push(`"${f.label}" depends on "${f.showIf.key}", which must come earlier`);
      }
      if ((f.type === 'info' || f.type === 'consent') && !f.text) {
        issues.push(`"${f.label}" needs its text`);
      }
      seen.add(f.key);
    }
  }
  return issues;
}

/* ------------------------------------------------------------------------ */
/* Answers                                                                   */
/* ------------------------------------------------------------------------ */

export const YES_NO = ['yes', 'no', 'unsure'] as const;
export const YesNoSchema = z.enum(YES_NO);
export type YesNo = z.infer<typeof YesNoSchema>;

export const SignatureAnswerSchema = z.object({
  kind: z.enum(['drawn', 'typed']),
  /** Printed name of the signer. */
  name: z.string().min(1).max(120),
  /** Drawn signature as SVG path data in a 600 x 200 box (vector, small). */
  path: z.string().max(60_000).optional(),
  signerRole: z.enum(['patient', 'guardian']),
  /** Guardian's relationship to the patient when a guardian signs. */
  guardianRelationship: z.string().max(60).optional(),
  signedAt: InstantSchema,
});
export type SignatureAnswer = z.infer<typeof SignatureAnswerSchema>;

export const SUBSCRIBER_RELATIONSHIPS = ['self', 'spouse', 'child', 'other'] as const;
export const SubscriberRelationshipSchema = z.enum(SUBSCRIBER_RELATIONSHIPS);
export type SubscriberRelationship = z.infer<typeof SubscriberRelationshipSchema>;

export const InsuranceCardAnswerSchema = z.object({
  hasInsurance: z.boolean(),
  frontAttachmentId: IdSchema.nullable().default(null),
  backAttachmentId: IdSchema.nullable().default(null),
  carrierName: z.string().max(120).default(''),
  payerId: z.string().max(20).default(''),
  /** Tenant carrier the patient confirmed, when the match was certain or chosen. */
  carrierId: IdSchema.nullable().default(null),
  memberId: z.string().max(40).default(''),
  groupNumber: z.string().max(40).default(''),
  relationship: SubscriberRelationshipSchema.default('self'),
  subscriberName: z.string().max(120).default(''),
  subscriberBirthdate: z.union([LocalDateSchema, z.literal('')]).default(''),
  subscriberEmployer: z.string().max(120).default(''),
  /** Values as OCR read them, kept so review can show "read from card". */
  ocr: z
    .object({
      carrierName: z.string(),
      payerId: z.string(),
      memberId: z.string(),
      groupNumber: z.string(),
      subscriberName: z.string(),
      rxBin: z.string(),
      confidence: z.number().min(0).max(1),
    })
    .nullable()
    .default(null),
});
export type InsuranceCardAnswer = z.infer<typeof InsuranceCardAnswerSchema>;

/**
 * Photos of a driver's license or state ID (front, back). The photos are
 * sealed attachments on the packet like insurance cards; staff see them in
 * review and file them to Documents. Answered once the front is uploaded.
 */
export const IdPhotoAnswerSchema = z.object({
  frontAttachmentId: IdSchema.nullable().default(null),
  backAttachmentId: IdSchema.nullable().default(null),
});
export type IdPhotoAnswer = z.infer<typeof IdPhotoAnswerSchema>;

/** Answers keyed by field key. Values are validated per field type by `checkAnswer`. */
export const AnswersSchema = z.record(z.string(), z.unknown());
export type Answers = Record<string, unknown>;

/** A problem with one answer, shown next to the field. */
export interface AnswerIssue {
  key: string;
  message: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function digitsOf(v: string): string {
  return v.replace(/\D/g, '');
}

/** True when the answer counts as given (required checks). */
export function isAnswered(field: FormField, value: unknown): boolean {
  switch (field.type) {
    case 'info':
      return true;
    case 'checkbox':
      return typeof value === 'boolean';
    case 'consent':
      return value === true;
    case 'checkboxes':
      return Array.isArray(value) && value.length > 0;
    case 'signature':
      return SignatureAnswerSchema.safeParse(value).success;
    case 'insurance_card': {
      const p = InsuranceCardAnswerSchema.safeParse(value);
      if (!p.success) return false;
      if (!p.data.hasInsurance) return true;
      return p.data.carrierName.trim() !== '' && p.data.memberId.trim() !== '';
    }
    case 'id_photo': {
      const p = IdPhotoAnswerSchema.safeParse(value);
      return p.success && p.data.frontAttachmentId !== null;
    }
    default:
      return typeof value === 'string' && value.trim() !== '';
  }
}

/**
 * Validates one answer against its field. Returns a patient-readable message
 * or null. Empty optional answers are fine; required ones are checked by
 * `missingRequired`.
 */
export function checkAnswer(field: FormField, value: unknown): string | null {
  if (value === undefined || value === null || value === '') return null;
  switch (field.type) {
    case 'short_text':
      return typeof value === 'string' && value.length <= 300
        ? null
        : 'Keep this under 300 characters.';
    case 'long_text':
      return typeof value === 'string' && value.length <= 4000
        ? null
        : 'Keep this under 4,000 characters.';
    case 'email':
      return typeof value === 'string' && EMAIL_RE.test(value.trim())
        ? null
        : 'Enter an email address like name@example.com.';
    case 'phone': {
      if (typeof value !== 'string') return 'Enter a phone number.';
      const d = digitsOf(value);
      return d.length === 10 || (d.length === 11 && d.startsWith('1'))
        ? null
        : 'Enter a 10-digit phone number.';
    }
    case 'date': {
      if (typeof value !== 'string' || !LocalDateSchema.safeParse(value).success) {
        return 'Enter a date as MM/DD/YYYY.';
      }
      const year = Number(value.slice(0, 4));
      return year >= 1900 && year <= 2100 ? null : 'Check the year.';
    }
    case 'select':
    case 'radio':
      return typeof value === 'string' && (field.options ?? []).some((o) => o.value === value)
        ? null
        : 'Pick one of the choices.';
    case 'checkboxes':
      return Array.isArray(value) &&
        value.every((v) => (field.options ?? []).some((o) => o.value === v))
        ? null
        : 'Pick from the choices.';
    case 'yes_no':
      return YesNoSchema.safeParse(value).success ? null : 'Answer Yes, No or Unsure.';
    case 'checkbox':
    case 'consent':
      return typeof value === 'boolean' ? null : 'Tick the box or leave it.';
    case 'signature':
      return SignatureAnswerSchema.safeParse(value).success ? null : 'Sign again, please.';
    case 'insurance_card': {
      const p = InsuranceCardAnswerSchema.safeParse(value);
      if (!p.success) return 'Check the insurance details.';
      const a = p.data;
      if (!a.hasInsurance) return null;
      if (a.relationship !== 'self') {
        if (a.subscriberName.trim() === '')
          return 'Enter the name of the person who holds the plan.';
        if (a.subscriberBirthdate === '') return 'Enter the plan holder’s date of birth.';
      }
      return null;
    }
    case 'id_photo':
      return IdPhotoAnswerSchema.safeParse(value).success ? null : 'Take the ID photos again.';
    case 'info':
      return null;
  }
}

/** Fields visible for these answers (`showIf` evaluated in order). */
export function visibleFields(section: FormSection, answers: Answers): FormField[] {
  return section.fields.filter((f) => isVisible(f, answers));
}

export function isVisible(field: FormField, answers: Answers): boolean {
  if (!field.showIf) return true;
  const v = answers[field.showIf.key];
  return v === field.showIf.equals;
}

/** Required visible fields with no answer, and answers that fail their checks. */
export function sectionIssues(section: FormSection, answers: Answers): AnswerIssue[] {
  const out: AnswerIssue[] = [];
  for (const f of visibleFields(section, answers)) {
    if (DISPLAY_ONLY_TYPES.has(f.type)) continue;
    const v = answers[f.key];
    const problem = checkAnswer(f, v);
    if (problem) out.push({ key: f.key, message: problem });
    else if (f.required && !isAnswered(f, v)) {
      out.push({
        key: f.key,
        message:
          f.type === 'consent'
            ? 'Please tick to agree before continuing.'
            : f.type === 'signature'
              ? 'Please sign here.'
              : 'This answer is needed.',
      });
    }
  }
  return out;
}

export function definitionIssuesForAnswers(def: FormDefinition, answers: Answers): AnswerIssue[] {
  return def.sections.flatMap((s) => sectionIssues(s, answers));
}

/** Share of answerable visible fields that have an answer, 0..1. */
export function answerProgress(def: FormDefinition, answers: Answers): number {
  let total = 0;
  let done = 0;
  for (const s of def.sections) {
    for (const f of visibleFields(s, answers)) {
      if (DISPLAY_ONLY_TYPES.has(f.type)) continue;
      total += 1;
      if (isAnswered(f, answers[f.key])) done += 1;
    }
  }
  return total === 0 ? 1 : done / total;
}

/** Every answerable field key in a definition. */
export function answerKeys(def: FormDefinition): string[] {
  return def.sections.flatMap((s) =>
    s.fields.filter((f) => !DISPLAY_ONLY_TYPES.has(f.type)).map((f) => f.key),
  );
}

/**
 * Carries answers into a new definition by field key (NP-13, NP-18).
 * Signatures and consents never carry over: they attest to a specific text.
 * Returns the kept answers and the keys that are new in this version.
 */
export function carryAnswers(
  previous: Answers,
  next: FormDefinition,
  previousDef: FormDefinition | null,
): { answers: Answers; newKeys: string[] } {
  const prevKeys = new Set(previousDef ? answerKeys(previousDef) : Object.keys(previous));
  const answers: Answers = {};
  const newKeys: string[] = [];
  for (const s of next.sections) {
    for (const f of s.fields) {
      if (DISPLAY_ONLY_TYPES.has(f.type)) continue;
      if (!prevKeys.has(f.key)) {
        newKeys.push(f.key);
        continue;
      }
      if (f.type === 'signature' || f.type === 'consent') continue;
      const v = previous[f.key];
      if (v !== undefined && checkAnswer(f, v) === null) answers[f.key] = v;
    }
  }
  return { answers, newKeys };
}

/** A label for an answer, as staff read it in review. */
export function displayAnswer(field: FormField, value: unknown): string {
  if (value === undefined || value === null || value === '') return '';
  switch (field.type) {
    case 'yes_no':
      return value === 'yes' ? 'Yes' : value === 'no' ? 'No' : value === 'unsure' ? 'Unsure' : '';
    case 'checkbox':
    case 'consent':
      return value === true ? 'Yes' : 'No';
    case 'select':
    case 'radio':
      return field.options?.find((o) => o.value === value)?.label ?? String(value);
    case 'checkboxes':
      return Array.isArray(value)
        ? value.map((v) => field.options?.find((o) => o.value === v)?.label ?? String(v)).join(', ')
        : '';
    case 'date':
      return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? `${value.slice(5, 7)}/${value.slice(8, 10)}/${value.slice(0, 4)}`
        : String(value);
    case 'signature': {
      const p = SignatureAnswerSchema.safeParse(value);
      return p.success ? `Signed by ${p.data.name}` : '';
    }
    case 'insurance_card': {
      const p = InsuranceCardAnswerSchema.safeParse(value);
      if (!p.success) return '';
      if (!p.data.hasInsurance) return 'No insurance';
      return [p.data.carrierName, p.data.memberId].filter(Boolean).join(', ');
    }
    case 'id_photo': {
      const p = IdPhotoAnswerSchema.safeParse(value);
      if (!p.success || !p.data.frontAttachmentId) return '';
      return p.data.backAttachmentId ? 'Front and back photos' : 'Front photo';
    }
    default:
      return typeof value === 'string' ? value : JSON.stringify(value);
  }
}

/* ------------------------------------------------------------------------ */
/* Templates and versions (staff)                                            */
/* ------------------------------------------------------------------------ */

export const VERSION_STATUSES = ['draft', 'published', 'retired'] as const;
export const VersionStatusSchema = z.enum(VERSION_STATUSES);

export const TemplateSummarySchema = z.object({
  id: IdSchema,
  name: z.string(),
  kind: FormKindSchema,
  description: z.string().nullable(),
  /** Built-in library key, for example `registration`; null for practice-made forms. */
  templateKey: z.string().nullable(),
  publishedVersion: z.number().int().nullable(),
  publishedAt: InstantSchema.nullable(),
  hasDraft: z.boolean(),
  draftVersion: z.number().int().nullable(),
  fieldCount: z.number().int(),
  syncFieldCount: z.number().int(),
  rules: AttachRulesSchema,
  /** Packets still open on an older version (shown before publishing). */
  pendingOnOlder: z.number().int(),
  hidden: z.boolean(),
  updatedAt: InstantSchema,
});
export type TemplateSummary = z.infer<typeof TemplateSummarySchema>;

export const TemplateListSchema = z.object({
  templates: z.array(TemplateSummarySchema),
  /**
   * Starter-library forms this practice does not have yet (PT-26). Reading the
   * list never installs them; `POST /forms/templates/library` does.
   */
  starterLibraryMissing: z.number().int().min(0).optional(),
});
export type TemplateList = z.infer<typeof TemplateListSchema>;

/**
 * Where an accepted answer for a record path goes at one office (PT-20):
 * - `velora`: written to the Velora record (no PMS, or data Velora owns);
 * - `pms`: sent to the PMS as part of one `patient.update` command;
 * - `manual`: kept for review, then a task asks staff to enter it in the PMS.
 */
export const FieldRouteSchema = z.enum(['velora', 'pms', 'manual']);
export type FieldRoute = z.infer<typeof FieldRouteSchema>;

export const OfficeWritebackSchema = z.object({
  locationId: IdSchema,
  name: z.string(),
  /** `Dentrix`, `Open Dental`, `Eaglesoft`, or null when the office has no PMS link. */
  pms: z.string().nullable(),
  /** Why PMS write-back of patient details is not available here, if it is not. */
  reason: z.string().nullable(),
  fields: z.record(RecordPathSchema, FieldRouteSchema),
});
export type OfficeWriteback = z.infer<typeof OfficeWritebackSchema>;

export const FormsWritebackSchema = z.object({ offices: z.array(OfficeWritebackSchema) });
export type FormsWriteback = z.infer<typeof FormsWritebackSchema>;

export const InstallLibraryResultSchema = z.object({ added: z.number().int().min(0) });

export const TemplateVersionSchema = z.object({
  id: IdSchema,
  version: z.number().int().positive(),
  status: VersionStatusSchema,
  definition: z.unknown(),
  definitionHash: z.string(),
  publishedAt: InstantSchema.nullable(),
  publishedBy: z.string().nullable(),
  createdAt: InstantSchema,
});
export type TemplateVersion = Omit<z.infer<typeof TemplateVersionSchema>, 'definition'> & {
  definition: FormDefinition;
};

export const TemplateDetailSchema = z.object({
  template: TemplateSummarySchema,
  versions: z.array(TemplateVersionSchema),
});
export type TemplateDetail = Omit<z.infer<typeof TemplateDetailSchema>, 'versions'> & {
  versions: TemplateVersion[];
};

export const CreateTemplateBodySchema = z.object({
  name: z.string().min(1).max(120),
  kind: FormKindSchema,
  /** Copy the published version of this template as the first draft. */
  copyFromId: IdSchema.optional(),
});

export const SaveDraftBodySchema = z.object({
  definition: z.unknown(),
  rules: AttachRulesSchema.optional(),
  name: z.string().min(1).max(120).optional(),
  /** Version of the draft the editor started from; a mismatch is a 409. */
  baseVersion: z.number().int().positive().nullable(),
});

export const PublishBodySchema = z.object({
  /** What happens to open packets on older versions (NP-13). */
  inFlight: z.enum(['keep', 'upgrade']),
});
export const PublishResultSchema = z.object({
  version: z.number().int(),
  upgradedPackets: z.number().int(),
  keptPackets: z.number().int(),
});
export type PublishResult = z.infer<typeof PublishResultSchema>;

export const DefinitionCheckSchema = z.object({ issues: z.array(z.string()) });

/* ------------------------------------------------------------------------ */
/* Packets (one per visit or per request)                                    */
/* ------------------------------------------------------------------------ */

export const PACKET_STATUSES = ['open', 'submitted', 'reviewed', 'revoked', 'expired'] as const;
export const PacketStatusSchema = z.enum(PACKET_STATUSES);
export type PacketStatus = z.infer<typeof PacketStatusSchema>;

export const ITEM_STATUSES = ['not_started', 'in_progress', 'completed'] as const;
export const ItemStatusSchema = z.enum(ITEM_STATUSES);
export type ItemStatus = z.infer<typeof ItemStatusSchema>;

/** Readiness as the schedule and flow board chip shows it (new-patient.md 4.4). */
export const READINESS_STATES = [
  'none',
  'incomplete',
  'review',
  'accepted',
  'sync_pending',
  'sync_failed',
] as const;
export const ReadinessStateSchema = z.enum(READINESS_STATES);
export type ReadinessState = z.infer<typeof ReadinessStateSchema>;

export const ReadinessSchema = z.object({
  state: ReadinessStateSchema,
  formsTotal: z.number().int(),
  formsCompleted: z.number().int(),
  /** Rows waiting for a staff decision. */
  openRows: z.number().int(),
  /** The medical history has positives not on the record (red +, NP-03). */
  newPositive: z.boolean(),
  /** A provider signed the medical-history review. */
  attested: z.boolean(),
  /** The appointment must not move to Complete yet (new positives, no sign-off). */
  blocksComplete: z.boolean(),
  /** Short chip text: "Forms 2/4", "Forms · Review", "Sync failed". */
  label: z.string(),
});
export type Readiness = z.infer<typeof ReadinessSchema>;

export const PacketItemSchema = z.object({
  id: IdSchema,
  templateId: IdSchema,
  name: z.string(),
  kind: FormKindSchema,
  version: z.number().int(),
  status: ItemStatusSchema,
  progress: z.number().min(0).max(1),
  completedAt: InstantSchema.nullable(),
});
export type PacketItem = z.infer<typeof PacketItemSchema>;

export const PacketSummarySchema = z.object({
  id: IdSchema,
  locationId: IdSchema,
  patientId: IdSchema.nullable(),
  patientName: z.string().nullable(),
  appointmentId: IdSchema.nullable(),
  appointmentStart: InstantSchema.nullable(),
  status: PacketStatusSchema,
  createdAt: InstantSchema,
  createdByName: z.string().nullable(),
  expiresAt: InstantSchema,
  submittedAt: InstantSchema.nullable(),
  revision: z.number().int(),
  items: z.array(PacketItemSchema),
  readiness: ReadinessSchema,
  /** One-time code to read to a patient opening the link on a new device. */
  pendingCode: z.object({ code: z.string(), expiresAt: InstantSchema }).nullable(),
  /** The link is locked after three wrong dates of birth. */
  locked: z.boolean(),
});
export type PacketSummary = z.infer<typeof PacketSummarySchema>;

export const PacketListSchema = z.object({ packets: z.array(PacketSummarySchema) });
export type PacketList = z.infer<typeof PacketListSchema>;

export const CreatePacketBodySchema = z.object({
  patientId: IdSchema,
  appointmentId: IdSchema.nullable().default(null),
  templateIds: z.array(IdSchema).min(1).max(12),
});

export const CreatedPacketSchema = z.object({
  packet: PacketSummarySchema,
  /** App path of the patient link (`/f/<token>`). Shown once; only a hash is stored. */
  linkPath: z.string(),
});
export type CreatedPacket = z.infer<typeof CreatedPacketSchema>;

export const SuggestionSchema = z.object({
  templateId: IdSchema,
  name: z.string(),
  kind: FormKindSchema,
  reason: z.string(),
  selected: z.boolean(),
});
export const SuggestionListSchema = z.object({
  suggestions: z.array(SuggestionSchema),
  appointment: z
    .object({
      id: IdSchema,
      start: InstantSchema,
      codes: z.array(z.string()),
      isNewPatient: z.boolean(),
    })
    .nullable(),
});
export type SuggestionList = z.infer<typeof SuggestionListSchema>;

export const PatientPickSchema = z.object({
  id: IdSchema,
  name: z.string(),
  birthdate: LocalDateSchema.nullable(),
  homeLocationId: IdSchema.nullable(),
  nextAppointment: z
    .object({ id: IdSchema, start: InstantSchema, locationId: IdSchema, isNewPatient: z.boolean() })
    .nullable(),
});
export const PatientPickListSchema = z.object({ patients: z.array(PatientPickSchema) });
export type PatientPick = z.infer<typeof PatientPickSchema>;

/* ------------------------------------------------------------------------ */
/* Review (staff diff, decisions, write-back state)                          */
/* ------------------------------------------------------------------------ */

export const ROW_KINDS = [
  /** Plain change: on file differs from the form. */
  'change',
  /** Medical: a positive answer with no matching active alert. */
  'new_positive',
  /** Medical: "No" to an active alert. Never auto-removed. */
  'no_longer_applies',
  /** Two plausible values (for example the card disagrees with the phone booking). */
  'pick',
] as const;
export const RowKindSchema = z.enum(ROW_KINDS);
export type RowKind = z.infer<typeof RowKindSchema>;

export const DECISIONS = ['accept', 'keep', 'remove'] as const;
export const DecisionSchema = z.enum(DECISIONS);
export type Decision = z.infer<typeof DecisionSchema>;

/**
 * Where an applied row went. `velora` rows are written directly; `pending`,
 * `sent`, `synced` and `failed` follow a PMS command; `manual` means the PMS
 * cannot take this change yet and a task asks staff to enter it there.
 */
export const APPLY_STATES = [
  'not_applied',
  'velora',
  'pending',
  'sent',
  'synced',
  'failed',
  'manual',
  'kept',
] as const;
export const ApplyStateSchema = z.enum(APPLY_STATES);
export type ApplyState = z.infer<typeof ApplyStateSchema>;

export const ReviewRowSchema = z.object({
  /** Stable row id within the packet (field key, or field key plus part). */
  id: z.string(),
  fieldKey: z.string(),
  category: ReviewCategorySchema,
  label: z.string(),
  kind: RowKindSchema,
  onFile: z.string().nullable(),
  fromForm: z.string().nullable(),
  /** Context under the label ("Read from card photo", "Guardian email"). */
  note: z.string().nullable(),
  /** Monospaced values (member IDs). */
  mono: z.boolean(),
  /** Clinical rows never default to accept and are skipped by "accept all clear". */
  clinical: z.boolean(),
  /** The decision a reviewer gets by pressing "accept all clear" (null: must decide). */
  safeDefault: DecisionSchema.nullable(),
  /** Decisions allowed on this row. */
  allowed: z.array(DecisionSchema),
  /** Permission needed for `remove` or `accept` on clinical rows. */
  needsPermission: z.string().nullable(),
  decision: DecisionSchema.nullable(),
  decidedBy: z.string().nullable(),
  decidedAt: InstantSchema.nullable(),
  applyState: ApplyStateSchema,
  /** Why the row went where it did, or why it failed. */
  applyNote: z.string().nullable(),
  commandId: IdSchema.nullable(),
});
export type ReviewRow = z.infer<typeof ReviewRowSchema>;

export const ReviewSignatureSchema = z.object({
  formName: z.string(),
  fieldLabel: z.string(),
  name: z.string(),
  signerRole: z.enum(['patient', 'guardian']),
  kind: z.enum(['drawn', 'typed']),
  path: z.string().nullable(),
  signedAt: InstantSchema,
  /** SHA-256 of the form version the patient saw (DO-09, NP-12). */
  definitionHash: z.string(),
  version: z.number().int(),
  ip: z.string().nullable(),
  device: z.string().nullable(),
});

export const CardImageSchema = z.object({
  side: z.enum(['front', 'back']),
  attachmentId: IdSchema,
  mime: z.string(),
  dataUrl: z.string(),
});

export const CarrierCandidateSchema = z.object({
  carrierId: IdSchema,
  name: z.string(),
  payerId: z.string().nullable(),
  confidence: z.number().min(0).max(1),
  reason: z.string(),
});
export type CarrierCandidate = z.infer<typeof CarrierCandidateSchema>;

export const ReviewNoteAnswerSchema = z.object({
  formName: z.string(),
  label: z.string(),
  value: z.string(),
});

export const ReviewSchema = z.object({
  packetId: IdSchema,
  locationId: IdSchema,
  patientId: IdSchema.nullable(),
  patientName: z.string().nullable(),
  appointmentId: IdSchema.nullable(),
  appointmentStart: InstantSchema.nullable(),
  forms: z.array(
    z.object({
      name: z.string(),
      version: z.number().int(),
      submittedAt: InstantSchema.nullable(),
      /** The form was updated since this patient last answered it (NP-18). */
      updatedSinceLast: z.boolean(),
    }),
  ),
  revision: z.number().int(),
  revisions: z.array(z.object({ revision: z.number().int(), submittedAt: InstantSchema })),
  submittedAt: InstantSchema.nullable(),
  from: z.string().nullable(),
  rows: z.array(ReviewRowSchema),
  /** Answers with no record path (NOTE fields), shown read-only. */
  notes: z.array(ReviewNoteAnswerSchema),
  signatures: z.array(ReviewSignatureSchema),
  cardImages: z.array(CardImageSchema),
  /** Photo ID images from `id_photo` fields (front, back). */
  idImages: z.array(CardImageSchema).optional(),
  carrierCandidates: z.array(CarrierCandidateSchema),
  writeTarget: z.object({
    kind: z.enum(['velora', 'pms']),
    pmsLabel: z.string().nullable(),
    /** Why PMS-mastered fields cannot be written back right now, if so. */
    blockedReason: z.string().nullable(),
  }),
  readiness: ReadinessSchema,
  attestation: z.object({ by: z.string(), at: InstantSchema, statement: z.string() }).nullable(),
  canClinical: z.boolean(),
  canInsurance: z.boolean(),
  done: z.boolean(),
});
export type Review = z.infer<typeof ReviewSchema>;

export const SaveDecisionsBodySchema = z.object({
  /** Revision the reviewer looked at; a newer submission is a 409 (NP-07). */
  revision: z.number().int().positive(),
  decisions: z.array(z.object({ rowId: z.string().min(1), decision: DecisionSchema })).max(200),
  /** Carrier picked for an insurance row when the match was not certain. */
  carrierId: IdSchema.nullable().optional(),
  /** Idempotency for the whole save (one per click). */
  idempotencyKey: z.string().min(8).max(120),
});

export const SaveDecisionsResultSchema = z.object({
  review: ReviewSchema,
  applied: z.number().int(),
  kept: z.number().int(),
  open: z.number().int(),
});
export type SaveDecisionsResult = z.infer<typeof SaveDecisionsResultSchema>;

export const AttestBodySchema = z.object({
  statement: z.string().min(1).max(400).default('Reviewed the medical history with the patient.'),
});

export const QueueItemSchema = z.object({
  packetId: IdSchema,
  locationId: IdSchema,
  patientId: IdSchema.nullable(),
  patientName: z.string().nullable(),
  appointmentStart: InstantSchema.nullable(),
  submittedAt: InstantSchema.nullable(),
  forms: z.array(z.string()),
  readiness: ReadinessSchema,
});
export type QueueItem = z.infer<typeof QueueItemSchema>;

export const QueueSchema = z.object({
  items: z.array(QueueItemSchema),
  counts: z.object({
    review: z.number().int(),
    syncFailed: z.number().int(),
    incomplete: z.number().int(),
    newPositive: z.number().int(),
  }),
});
export type Queue = z.infer<typeof QueueSchema>;

export const AppointmentReadinessListSchema = z.object({
  appointments: z.array(
    z.object({ appointmentId: IdSchema, packetId: IdSchema, readiness: ReadinessSchema }),
  ),
});

/* ------------------------------------------------------------------------ */
/* Public patient link                                                       */
/* ------------------------------------------------------------------------ */

export const FORM_SESSION_HEADER = 'x-velora-form-session';

export const PUBLIC_GATES = ['dob', 'code', 'ready', 'locked'] as const;

/** What an unverified visitor may see: the practice, never the patient's answers. */
export const PublicLandingSchema = z.object({
  valid: z.literal(true),
  practiceName: z.string(),
  officeName: z.string(),
  officePhone: z.string().nullable(),
  /** First name and last initial only. */
  patientLabel: z.string(),
  appointmentLabel: z.string().nullable(),
  formCount: z.number().int(),
  gate: z.enum(PUBLIC_GATES),
  /** The office's patient portal sign-in page, when the office has the portal on. */
  portalUrl: z.string().nullable().optional(),
});
export type PublicLanding = z.infer<typeof PublicLandingSchema>;

export const VerifyDobBodySchema = z.object({
  birthdate: LocalDateSchema,
  /** Random id the browser keeps for this device (never PHI). */
  deviceId: z.string().min(16).max(80),
  /** Cloudflare Turnstile token when forms links are protected. */
  turnstileToken: TurnstileTokenSchema,
});

export const VerifyResultSchema = z.object({
  gate: z.enum(PUBLIC_GATES),
  /** Session token once the gate is `ready`. Sent back in `x-velora-form-session`. */
  session: z.string().nullable(),
  attemptsLeft: z.number().int().nullable(),
  message: z.string().nullable(),
});
export type VerifyResult = z.infer<typeof VerifyResultSchema>;

export const VerifyCodeBodySchema = z.object({
  /** The date of birth is checked again with the code (the gate keeps no state between steps). */
  birthdate: LocalDateSchema,
  code: z.string().regex(/^\d{6}$/),
  deviceId: z.string().min(16).max(80),
});

export const PublicItemSchema = z.object({
  id: IdSchema,
  name: z.string(),
  kind: FormKindSchema,
  version: z.number().int(),
  definition: z.unknown(),
  answers: AnswersSchema,
  /** Questions added since the patient's last answers (highlighted, NP-18). */
  newKeys: z.array(z.string()),
  step: z.number().int().min(0),
  status: ItemStatusSchema,
});
export type PublicItem = Omit<z.infer<typeof PublicItemSchema>, 'definition'> & {
  definition: FormDefinition;
};

export const PublicPacketSchema = z.object({
  practiceName: z.string(),
  officeName: z.string(),
  patientFirstName: z.string(),
  isMinor: z.boolean(),
  appointmentLabel: z.string().nullable(),
  status: PacketStatusSchema,
  /** Answers can still be changed (until staff accept them, NP-07). */
  editable: z.boolean(),
  revision: z.number().int(),
  items: z.array(PublicItemSchema),
  /** Where to resume: item index and step within it (NP-08). */
  resume: z.object({ item: z.number().int(), step: z.number().int() }),
  carriers: z.array(z.object({ id: IdSchema, name: z.string(), payerId: z.string().nullable() })),
});
export type PublicPacket = Omit<z.infer<typeof PublicPacketSchema>, 'items'> & {
  items: PublicItem[];
};

export const SaveAnswersBodySchema = z.object({
  answers: AnswersSchema,
  step: z.number().int().min(0).max(60),
  /** Marks the form completed (all required answers present). */
  complete: z.boolean().default(false),
});
export const SaveAnswersResultSchema = z.object({
  savedAt: InstantSchema,
  status: ItemStatusSchema,
  issues: z.array(z.object({ key: z.string(), message: z.string() })),
});

export const UploadBodySchema = z.object({
  side: z.enum(['front', 'back']),
  /** What the photo is of: an insurance card (default) or a photo ID (`id_photo` field). */
  purpose: z.enum(['insurance', 'id']).default('insurance'),
  /** `data:image/...;base64,...`, at most about 6 MB (the page downsizes photos first). */
  dataUrl: z.string().max(8_500_000),
});
export const UploadResultSchema = z.object({ attachmentId: IdSchema });

export const OcrBodySchema = z.object({
  frontAttachmentId: IdSchema,
  backAttachmentId: IdSchema.nullable().default(null),
});
export const OcrResultSchema = z.object({
  read: z.boolean(),
  fields: z.object({
    carrierName: z.string(),
    payerId: z.string(),
    memberId: z.string(),
    groupNumber: z.string(),
    subscriberName: z.string(),
    rxBin: z.string(),
  }),
  confidence: z.number().min(0).max(1),
  candidates: z.array(CarrierCandidateSchema),
  /** The best match is certain enough to show as "X, payer N? Yes / change". */
  matched: CarrierCandidateSchema.nullable(),
  message: z.string().nullable(),
  /**
   * False when automatic card reading is not available on this server (the
   * mock reader is off in production, PT-20): the form stops trying and the
   * patient types the details.
   */
  available: z.boolean().optional(),
});
export type OcrResult = z.infer<typeof OcrResultSchema>;

export const SubmitResultSchema = z.object({
  revision: z.number().int(),
  submittedAt: InstantSchema,
});
